import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

type GalleryItem = {
  id: number;
  type: 'image' | 'video';
  src: string;
  category: string;
  title: string;
  description: string;
  videoId?: string; // YouTube video ID for video type
};

export const galleryItems: GalleryItem[] = [
  // YouTube Videos
  {
    id: 3,
    type: 'video',
    src: 'https://youtu.be/wB4vLuSNBJM?si=5xioZB1vdDziefEz',
    videoId: 'wB4vLuSNBJM',
    category: 'youtube',
    title: 'ID Consulting Event',
    description: 'Highlights from our recent ID consulting event.',
  },
  {
    id: 7,
    type: 'video',
    src: 'https://youtu.be/XrQW6dIIYiQ?si=hWx6GToz5diBYeWz',
    videoId: 'XrQW6dIIYiQ',
    category: 'Music Video',
    title: 'Seth Adeyi ft. 7Dime - All of you [Official Music Video]',
    description: 'A captivating music video blending visuals and sound.',
  },
  {
    id: 8,
    type: 'video',
    src: 'https://youtu.be/ERbvB4h-dYM?si=BN4hVsUg0wLD5ET9',
    videoId: 'ERbvB4h-dYM',
    category: 'Music Video',
    title: 'Paul Rarity - BIG NAME feat. Reblah & Protek Illasheva',
    description: 'A powerful collaboration showcasing talent and creativity.',
  },
  {
    id: 9,
    type: 'video',
    src: 'https://youtu.be/XZMELuAuPmM?si=is1zShlRwHWyZXuo',
    videoId: 'XZMELuAuPmM',
    category: 'Music Concert',
    title: 'DGONG LIVE',
    description: 'An electrifying live concert experience.',
  },
  {
    id: 10,
    type: 'video',
    src: 'https://youtu.be/64a1NvQjqk0?si=n7Mf57PGmObTIeTb',
    videoId: '64a1NvQjqk0',
    category: 'Documentary',
    title: 'KEAMES STORY DOCUMENTARY TEASER',
    description: 'A teaser for an inspiring documentary project.',
  },
  {
    id: 13,
    type: 'video',
    src: 'https://youtu.be/8qCD1QJliEY?si=J7Z7qHVAFsJTB36t',
    videoId: '8qCD1QJliEY',
    category: 'Wedding',
    title: 'OYIN & TONY',
    description: 'A beautiful wedding video capturing the love story of Oyin and Tony.',
  },
  {
    id: 14,
    type: 'video',
    src: 'https://youtu.be/OMxS5h9UT7c?si=QhXp46e4KpbWovqK',
    videoId: 'OMxS5h9UT7c',
    category: 'Short Film',
    title: 'THE GENZ AFRICAN QUEEN',
    description: '',
  },
  {
    id: 15,
    type: 'video',
    src: 'https://youtu.be/kURPbAeUdZI?si=3aaezjEFOyvBWjBe',
    videoId: 'kURPbAeUdZI',
    category: 'Event',
    title: 'Diplomatic Collaboration for Impact - The REA-HUAWEI Partnership',
    description: '',
  },
  {
    id: 16,
    type: 'video',
    src: 'https://youtu.be/0SfLuAt8R7M?si=8oeOYiT-Y02ZnSwe',
    videoId: '0SfLuAt8R7M',
    category: 'Music Video',
    title: 'Benny Ats - Keep Me Safe Online (Official Music Video)',
    description: '',
  },
  {
    id: 17,
    type: 'video',
    src: 'https://youtu.be/OLo1QvfRksg?si=iw4CI3Ji6fciHOti',
    videoId: 'OLo1QvfRksg',
    category: 'Stage Play',
    title: "Lurk in the Dark",
    description: '',
  },
  {
    id: 18,
    type: 'video',
    src: 'https://youtu.be/cnKAfg1M2VI?si=1nypGiNZBI9EGmKb',
    videoId: 'cnKAfg1M2VI',
    category: 'Message',
    title: "Message from GIZ Nigeria & ECOWAS Country Director",
    description: 'Activism against Gender Based Violence',
  },
];


const ITEMS_PER_PAGE = 12;

const OurWork = () => {
  const [selectedItem, setSelectedItem] = useState<GalleryItem | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [preloadedVideos, setPreloadedVideos] = useState<Set<string>>(new Set());

  // Calculate pagination
  const totalPages = Math.ceil(galleryItems.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedItems = galleryItems.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Reset to first page when category changes

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Preload video when hovering over thumbnail
  const preloadVideo = (videoId: string) => {
    if (!preloadedVideos.has(videoId)) {
      // Preconnect to YouTube domains
      const preconnectLink1 = document.createElement('link');
      preconnectLink1.rel = 'preconnect';
      preconnectLink1.href = 'https://www.youtube-nocookie.com';
      document.head.appendChild(preconnectLink1);

      const preconnectLink2 = document.createElement('link');
      preconnectLink2.rel = 'preconnect';
      preconnectLink2.href = 'https://www.youtube.com';
      document.head.appendChild(preconnectLink2);

      // Create a hidden iframe to preload the video
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=0&rel=0&enablejsapi=1&origin=${window.location.origin}`;
      iframe.style.display = 'none';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      document.body.appendChild(iframe);

      setPreloadedVideos(prev => new Set([...prev, videoId]));

      // Clean up after a short delay
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
        document.head.removeChild(preconnectLink1);
        document.head.removeChild(preconnectLink2);
      }, 2000);
    }
  };

  // Handle opening video in lightbox
  const openModal = (item: GalleryItem) => {
    setSelectedItem(item);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';

    if (item.type === 'video' && item.videoId) {
      setIsVideoLoading(true);
      preloadVideo(item.videoId);
    }
  };

  // Close lightbox
  const closeModal = () => {
    setLightboxOpen(false);
    setIsVideoLoading(false);
    document.body.style.overflow = 'auto';
  };

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-dark-gray to-gray-900">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Our <span className="text-burnt-orange">Work</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              A showcase of our latest projects and creative work.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Gallery Filters */}
      <section className="py-12 bg-dark-gray">
        <div className="container mx-auto px-4">
          {/* Gallery Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
            {paginatedItems.map((item) => (
              <div
                key={item.id}
                className="relative group cursor-pointer overflow-hidden rounded-lg shadow-lg transform transition-all duration-300 hover:scale-105"
                onClick={() => openModal(item)}
                onMouseEnter={() => {
                  // setHoveredItem(item.id);
                  if (item.type === 'video' && item.videoId) {
                    preloadVideo(item.videoId);
                  }
                }}
                // onMouseLeave={() => setHoveredItem(null)}
              >
                {item.type === 'video' ? (
                  <>
                    <div className="relative w-full h-64 overflow-hidden">
                      <img
                        src={`https://img.youtube.com/vi/${item.videoId}/maxresdefault.jpg`}
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center transform transition-transform group-hover:scale-110">
                          <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <img
                    src={item.src}
                    alt={item.title}
                    className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col justify-end p-4">
                  <h3 className="text-white font-semibold text-lg">{item.title}</h3>
                  <p className="text-gray-300 text-sm capitalize">{item.category}</p>
                </div>
              </div>
            ))}
          </div>


          <div className="col-span-full flex justify-center mt-8">
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 rounded-md bg-gray-800 text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
              >
                Previous
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                // Show first page, last page, current page, and one page before/after current
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-10 h-10 rounded-md flex items-center justify-center ${currentPage === pageNum
                      ? 'bg-burnt-orange text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      } transition-colors`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              {totalPages > 5 && currentPage < totalPages - 2 && (
                <span className="px-2 text-gray-400">...</span>
              )}

              {totalPages > 5 && currentPage < totalPages - 1 && (
                <button
                  onClick={() => handlePageChange(totalPages)}
                  className={`w-10 h-10 rounded-md flex items-center justify-center ${currentPage === totalPages
                    ? 'bg-burnt-orange text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    } transition-colors`}
                >
                  {totalPages}
                </button>
              )}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 rounded-md bg-gray-800 text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
              >
                Next
              </button>
            </div>
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-dark-gray to-gray-900">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Like What You See?</h2>
            <p className="text-xl text-gray-300 mb-8">
              Let's create something amazing together. Get in touch to discuss your next project.
            </p>
            <a
              href="/contact"
              className="inline-block bg-burnt-orange hover:bg-burnt-orange/90 text-white font-semibold py-3 px-8 rounded-md transition-colors"
            >
              Get In Touch
            </a>
          </motion.div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {lightboxOpen && selectedItem && (
          <motion.div
            className="fixed inset-0 bg-black/90 z-50 flex pt-32 justify-center p-4 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <div
              className="relative w-full max-w-5xl mb-8 flex flex-col items-center"
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={closeModal}
                className="absolute -top-8 right-0 text-white hover:text-burnt-orange transition-colors z-10"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {selectedItem.type === 'video' && selectedItem.videoId ? (
                <div className="relative w-full flex-grow min-h-0">
                  <div className="relative w-full h-full">
                    {isVideoLoading && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-burnt-orange"></div>
                      </div>
                    )}
                    <div className="w-full h-full flex items-center justify-center">
                      <iframe
                        className={`w-full h-full min-h-[50vh] max-h-[60vh] ${isVideoLoading ? 'opacity-0' : 'opacity-100'} transition-opacity`}
                        src={`https://www.youtube-nocookie.com/embed/${selectedItem.videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&origin=${window.location.origin}`}
                        title={selectedItem.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                        onLoad={(e) => {
                          setIsVideoLoading(false);
                          const iframe = e.target as HTMLIFrameElement;
                          if (iframe.contentDocument?.body?.innerText.includes('An error occurred')) {
                            window.open(`https://www.youtube.com/watch?v=${selectedItem.videoId}`, '_blank');
                            closeModal();
                          }
                        }}
                        onError={() => {
                          setIsVideoLoading(false);
                          window.open(`https://www.youtube.com/watch?v=${selectedItem.videoId}`, '_blank');
                          closeModal();
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex-grow min-h-0 flex items-center justify-center">
                  <img
                    src={selectedItem.src}
                    alt={selectedItem.title}
                    className="max-w-full max-h-[60vh] object-contain"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = 'https://placehold.co/800x600/1f2937/6b7280?text=Image+Not+Found';
                    }}
                  />
                </div>
              )}

              <div className="mt-4 text-white text-center px-4 py-2">
                <h3 className="text-xl font-bold line-clamp-1" title={selectedItem.title}>{selectedItem.title}</h3>
                <p className="text-burnt-orange text-xs uppercase tracking-wider mb-2">{selectedItem.category}</p>
                <p className="text-gray-300 text-sm mb-3 line-clamp-2" title={selectedItem.description}>
                  {selectedItem.description}
                </p>

                <div className="flex justify-center space-x-3">
                  {selectedItem.type === 'video' ? (
                    <a
                      href="https://www.youtube.com/@samfrenojo"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-sm transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                      </svg>
                      YouTube
                    </a>
                  ) : (
                    <a
                      href="https://www.instagram.com/samfrenojomedia/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded text-sm transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                      Instagram
                    </a>
                  )}
                </div>
              </div>

              <button
                className="absolute left-2 md:-left-10 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  const currentIndex = galleryItems.findIndex(item => item.id === selectedItem.id);
                  const prevIndex = (currentIndex - 1 + galleryItems.length) % galleryItems.length;
                  setSelectedItem(galleryItems[prevIndex]);
                }}
                aria-label="Previous item"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <button
                className="absolute right-2 md:-right-10 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  const currentIndex = galleryItems.findIndex(item => item.id === selectedItem.id);
                  const nextIndex = (currentIndex + 1) % galleryItems.length;
                  setSelectedItem(galleryItems[nextIndex]);
                }}
                aria-label="Next item"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OurWork;
